/**
 * <s-product-recommendations> fetches Shopify's recommendations JSON endpoint,
 * then renders each recommended product through templates/product.card.liquid.
 *
 * Element dataset:
 *   data-url        — routes.product_recommendations_url written by Liquid.
 *   data-product-id — Current product ID. Required by the recommendations route.
 *   data-intent     — Recommendation intent. Default: related.
 *   data-limit      — Recommendation count. Default: 4.
 */
import { transition } from 'view-transition';

class ProductRecommendations extends HTMLElement {
  #ac;
  #observer;
  #loaded = false;

  connectedCallback() {
    this.#ac = new AbortController();
    if (this.#loaded) return;

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries, observer) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        observer.unobserve(this);
        this.#observer = null;
        this.#fetchRecommendations();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.#observer.observe(this);
      return;
    }

    this.#fetchRecommendations();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#ac?.abort();
  }

  async #fetchRecommendations() {
    const url = this.#recommendationsUrl();
    if (!url) return;

    const signal = this.#ac.signal;

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) throw new Error(`Recommendations fetch failed: ${response.status}`);

      const data = await response.json();
      signal.throwIfAborted();

      const products = Array.isArray(data.products) ? data.products : [];
      if (!products.length) {
        await transition(() => this.replaceChildren());
        this.#loaded = true;
        return;
      }

      const cardPromises = [];
      for (const product of products) {
        cardPromises.push(this.#fetchProductCard(product, signal));
      }

      const cards = (await Promise.all(cardPromises)).filter(Boolean);
      signal.throwIfAborted();

      await transition(() => this.#render(cards));
      this.#loaded = true;
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[s-product-recommendations] Recommendations fetch failed:', error);
    }
  }

  async #fetchProductCard(product, signal) {
    const url = this.#productCardUrl(product);
    if (!url) return null;

    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });
    if (!response.ok) throw new Error(`Product card fetch failed: ${response.status}`);

    const text = await response.text();
    signal.throwIfAborted();

    const doc = new DOMParser().parseFromString(text, 'text/html');
    const card = doc.querySelector('[data-recommendation-card]');
    if (!card) return null;

    this.#syncRecommendationLinks(card, product.url);
    return card;
  }

  #recommendationsUrl() {
    const productId = this.dataset.productId;
    const route = this.dataset.url;
    if (!productId || !route) return null;

    const url = new URL(route, window.location.origin);
    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
    }

    url.searchParams.set('product_id', productId);
    url.searchParams.set('intent', this.dataset.intent || 'related');
    url.searchParams.set('limit', this.dataset.limit || '4');
    return url;
  }

  #productCardUrl(product) {
    if (!product?.url) return null;

    const url = new URL(product.url, window.location.origin);
    // ?view=card selects templates/product.card.liquid; rendered card links keep the recommendation URL.
    url.searchParams.set('view', 'card');
    return url;
  }

  #render(cards) {
    if (!cards.length) {
      this.replaceChildren();
      return;
    }

    const template = this.querySelector('template[data-recommendations-shell]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const list = fragment.querySelector('[data-recommendations-list]');
    if (!list) return;

    list.replaceChildren(...cards);
    this.replaceChildren(fragment);
  }

  #syncRecommendationLinks(card, recommendationUrl) {
    if (!recommendationUrl) return;

    const target = new URL(recommendationUrl, window.location.origin);
    target.searchParams.delete('view');
    const targetPath = target.pathname.replace(/\/$/, '');

    for (const element of card.querySelectorAll('a[href], [data-product-url]')) {
      const attribute = element.hasAttribute('href') ? 'href' : 'data-product-url';
      const current = new URL(element.getAttribute(attribute), window.location.origin);
      if (current.pathname.replace(/\/$/, '') !== targetPath) continue;

      const next = new URL(target.toString());
      for (const [key, value] of current.searchParams.entries()) {
        if (key === 'view') continue;
        if (!next.searchParams.has(key)) next.searchParams.set(key, value);
      }
      element.setAttribute(attribute, `${next.pathname}${next.search}${next.hash}`);
    }
  }
}

customElements.define('s-product-recommendations', ProductRecommendations);
